import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import ScriptTemplateForm from "./form";
import {
  blankScriptTemplate,
  validate,
  scrollToField,
  withClientIds,
} from "./form_helpers";
import {
  PageContainer,
  PageTopbar,
  StickyActionBar,
  astryx,
  neutralTheme,
  useDocumentTheme,
  type IScriptTemplate
} from "@shadowcue/ui";

const responseStatus = (err: unknown): number | null => {
  if (
    err !== null &&
    typeof err === "object" &&
    "response" in err &&
    err.response !== null &&
    typeof err.response === "object" &&
    "status" in err.response &&
    typeof err.response.status === "number"
  ) {
    return err.response.status;
  }
  return null;
};

const responseData = (err: unknown): Record<string, unknown> => {
  if (
    err !== null &&
    typeof err === "object" &&
    "response" in err &&
    err.response !== null &&
    typeof err.response === "object" &&
    "data" in err.response &&
    err.response.data !== null &&
    typeof err.response.data === "object"
  ) {
    return err.response.data as Record<string, unknown>;
  }
  return {};
};

interface EditScriptTemplateProps {
  currentUser: IUser;
}

type SubmitError =
  | { type: "locked"; latestRevisionId: number | null }
  | { type: "generic" }
  | null;

const axios = newAxiosClient();

const EditScriptTemplate = ({ currentUser }: EditScriptTemplateProps) => {
  const { scriptTemplateId } = useParams();
  // Seeded with blankScriptTemplate (rather than null) so the JSX below can
  // read formValues unconditionally — ContentContainer's `children` prop is
  // constructed on every render regardless of `loading`, so a null initial
  // value would throw before the fetch below ever resolves.
  const [formValues, setFormValues] = useState<IScriptTemplate>(blankScriptTemplate);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError>(null);
  const navigate = useNavigate();
  const mode = useDocumentTheme();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`/api/script_templates/${scriptTemplateId}`, {
          headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
        });
        setFormValues(withClientIds(res.data.scriptTemplate as IScriptTemplate));
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [scriptTemplateId]);

  const clearError = (key: string) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async () => {
    const validationErrors = validate(formValues);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollToField(Object.keys(validationErrors)[0]);
      return;
    }

    setErrors({});

    const payload = {
      script_template: {
        name: formValues.name,
        description: formValues.description,
        notes: formValues.notes,
        scriptTemplateSectionsAttributes: formValues.scriptTemplateSections.map(
          (section) => ({
            name: section.name,
            scriptTemplateQuestionsAttributes:
              section.scriptTemplateQuestions.map((question) => ({
                body: question.body,
                prospectiveResponse: question.prospectiveResponse,
              })),
          }),
        ),
      },
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.patch(`/api/script_templates/${scriptTemplateId}`, payload, {
        headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
        withCredentials: true,
      });
      navigate("/admin/script_templates");
    } catch (err) {
      if (responseStatus(err) === 423) {
        const data = responseData(err);
        const latestRevisionId =
          typeof data.latestRevisionId === "number" ? data.latestRevisionId : null;
        setSubmitError({ type: "locked", latestRevisionId });
      } else {
        setSubmitError({ type: "generic" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const shouldRenderRevisionError = (() => {
    if (submitError?.type === "locked") return true;

    // If latestActiveRevisionId is not set, this is the latest revision.
    if (!formValues.latestActiveRevisionId) return false;

    return formValues.id !== formValues.latestActiveRevisionId;
  })();

  const submitErrorMessage: string | undefined = (() => {
    if (submitError?.type === "locked") {
      return "This template was changed elsewhere and is locked.";
    }
    if (submitError?.type === "generic") {
      return "Failed to save the template. Please try again.";
    }
    return undefined;
  })();

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <astryx.LayerProvider>
        <PageContainer>
          <PageTopbar
            breadcrumbs={[
              { label: "Script templates", href: "/admin/script_templates" },
              { label: "Edit" },
            ]}
          />

          {shouldRenderRevisionError && (
            <astryx.Banner
              status="warning"
              title="This is not the latest revision and cannot be edited."
              description={
                <Link to={`/admin/script_templates/${formValues.latestActiveRevisionId}/edit`}>
                  Edit the latest revision here.
                </Link>
              }
            />
          )}

          <ScriptTemplateForm
            values={formValues}
            onChange={setFormValues}
            errors={errors}
            onClearError={clearError}
            loading={loading}
            isEditing
            readonly={shouldRenderRevisionError}
          />

          {!shouldRenderRevisionError && (
            <StickyActionBar error={submitErrorMessage}>
              <astryx.Button
                label="Cancel"
                variant="ghost"
                onClick={() => navigate("/admin/script_templates")}
              />
              <astryx.Button
                label="Save"
                variant="primary"
                isLoading={submitting}
                onClick={handleSubmit}
              />
            </StickyActionBar>
          )}
        </PageContainer>
      </astryx.LayerProvider>
    </astryx.Theme>
  );
};

export default EditScriptTemplate;
